import React, { useState } from "react";
import {
    CardTitle,
    CardBody,
    Modal,
    ModalHeader,
    ModalBody,
    Button,
    Label,
    Level,
    LevelItem,
    Divider
} from "@patternfly/react-core";
import {
    DownloadIcon,
    ExternalLinkAltIcon
} from "@patternfly/react-icons";

import { getMediaUrl } from "../../utils/media";
import { getMonths } from "shared/utils/monthsUtils";
import { GroupsCell } from "../table/columns/GroupsCell";
import { SheetAnswerKeyCell } from "../table/columns/SheetAnswerKeyCell";
import { SheetTypesCell } from "../table/columns/SheetTypesCell";

import { DevAttachmentCell } from "../table/columns/DevAttachmentCell";
import { DevSolutionsCell } from "../table/columns/DevSolutionsCell";
import { SheetNameCell } from "../table/columns/SheetNameCell";

import { ShareCell } from "../table/columns/ShareCell";

import { useBranch } from "../../hooks/useBranch";

import fallbackImg from 'shared/assets/images/webp/fallback-img-pl-black.webp';

export const SheetCard = ({ row, props, context, Icons, setSheets, groupFilter, viewMode, isSelected }) => {
    const branch = useBranch();
    const hasFiles =
        Boolean(row?.answer_key_url?.length) ||
        Boolean(row?.attachment_url?.length);

    const groupsCompleted = React.useMemo(() => {
        const rawGroups = row?.groups_completed || {};
        // filtrujemy stare grupy
        return Object.fromEntries(
            Object.entries(rawGroups).filter(
            ([_, item]) => item && !item.group_symbol?.toUpperCase().startsWith("ABS")
            )
        );
        }, [row?.groups_completed]);

    const hasGroups = Object.values(groupsCompleted || {}).length > 0;

    const sheetName = row?.name || "Brak nazwy";
    
    const miniature = row?.raw?.sheet_miniature;
    const sheetUrl = row?.raw?.sheet_url;
    
    const miniatureUrl = miniature
    ? miniature.includes("/miniature/") // jeśli nowa struktura
        ? `${props.state.api_url}${miniature}`  // nowy URL
        : `${props.state.api_url}/${props.state.django_media_dir}/${miniature}` // stara struktura
    : null;

    // const [imgSrc, setImgSrc] = useState(
    //     miniature
    //         ? miniatureUrl
    //         : fallbackImage
    // );
    const [imgSrc, setImgSrc] = useState(
        miniature ? miniatureUrl : fallbackImg
    );

    const [loaded, setLoaded] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const handleDownload = () => {
        if (!sheetUrl) return;
        getMediaUrl(
            `${props.state.api_url}/${props.state.django_media_dir}/${sheetUrl}`,
            sheetName
        );
    };

    const hasSolutions =
        (row.raw.html_css_url && row.raw.html_css_url !== "") ||
        (row.raw.angular_url && row.raw.angular_url !== "None") ||
        (row.raw.react_url && row.raw.react_url !== "None") ||
        (row.raw.solution_url && row.raw.solution_url !== "None");

    return (
        <>
        <CardTitle
        >
            <Level
                style={{
                    paddingTop: "10px",
                    paddingBottom: "5px",
                    fontSize: "13px",
                    // color: "#cfcfcf",
                    lineHeight: "1.6"
                }}
            >
                <LevelItem><b>Arkusz: </b></LevelItem>
                <LevelItem>
                    <SheetNameCell row={row} props={props} context={context} withMiniature ={false} withPopover={false} />
                </LevelItem>
            </Level>
        </CardTitle>
        <CardBody>
            {/* {row?.raw?.qualification_type === "dev" && ( */}
            {miniature && (
            <>
            {/* IMAGE CONTAINER */}
            <div
                className="ultra-card-image"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsPreviewOpen(true);
                }}
                style={{
                    position: "relative",
                    height: "190px",
                    borderRadius: "14px",
                    overflow: "hidden",
                    cursor: "zoom-in",
                }}
            >
                {/* LOADING GLASS OVERLAY */}
                {!loaded && (
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            backdropFilter: "blur(12px)",
                            background:
                                "linear-gradient(90deg,#2a2a2a,#3a3a3a,#2a2a2a)",
                            backgroundSize: "200% 100%",
                            animation: "shimmer 1.5s infinite"
                        }}
                    />
                )}

                {/* IMAGE */}
                <img
                    src={imgSrc}
                    alt={sheetName}
                    loading="lazy"
                    onLoad={() => setLoaded(true)}
                    onError={() => setImgSrc(fallbackImg)}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "0.6s ease",
                        filter: loaded
                            ? "grayscale(20%) brightness(105%)"
                            : "grayscale(90%)"
                    }}
                />

                {/* ENTERPRISE FLOATING ACTION BAR */}
                <div
                    className="ultra-actions"
                    style={{
                        position: "absolute",
                        bottom: "12px",
                        right: "12px",
                        display: "flex",
                        gap: "8px",
                        opacity: 0,
                        transform: "translateY(10px)",
                        transition: "0.35s ease"
                    }}
                >
                    <Button
                        variant="secondary"
                        icon={<ExternalLinkAltIcon />}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsPreviewOpen(true);
                        }}
                    />

                    <Button
                        variant="primary"
                        icon={<DownloadIcon />}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDownload();
                        }}
                    />
                </div>
            </div>
            </>
            )}
            {/* META INFO PANEL */}
            <Level
                style={{
                    paddingTop: "10px",
                    fontSize: "13px",
                    // color: "#cfcfcf",
                    lineHeight: "1.6"
                }}
            >
                <LevelItem>
                    {isSelected 
                        ? (
                            <>
                            <b>Id: </b>
                            <Label
                                isCompact
                                variant="outline"
                                status="success"
                                style={{
                                    marginBottom: "5px"
                                }}
                            >
                                {row.id}
                            </Label>
                            </>
                        ) : (
                            <>
                            <b>Id:</b> {row.id}
                            </>
                        )
                    }
                </LevelItem>
            </Level>
            <Divider />
            <Level
                style={{
                    paddingTop: "10px",
                    fontSize: "13px",
                    // color: "#cfcfcf",
                    lineHeight: "1.6"
                }}
            >
                <LevelItem><b>Rok:</b> {row.year}</LevelItem>
                <LevelItem><b>Sesja:</b> {getMonths(row.exam_month)}</LevelItem>
                <LevelItem><b>Nr:</b> {row.raw?.task_number?.toString().padStart(2, "0")}</LevelItem>
            </Level>
            <Divider />
            {hasFiles && (
                <>
                <Level
                    style={{
                        paddingTop: "10px",
                        fontSize: "13px",
                        // color: "#cfcfcf",
                        lineHeight: "1.6"
                    }}
                >
                    {row?.answer_key_url && (
                        <LevelItem>
                            <b>Klucz:</b>{' '}
                            <SheetAnswerKeyCell row={row} props={props} context={context} />
                        </LevelItem>
                    )}
                    {row?.attachment_url && row?.raw?.qualification_type === "dev" && (
                        <LevelItem>
                            <b>Załącznik:</b>{' '}
                            <DevAttachmentCell row={row} props={props}  context={context}  />
                        </LevelItem>
                    )}
                </Level>

                <Divider />
                </>
            )}
            {row?.raw?.sheet_types?.length > 0 &&
            <>
            <Level
                style={{
                    paddingTop: "10px",
                    paddingBottom: "5px",
                    fontSize: "13px",
                    // color: "#cfcfcf",
                    lineHeight: "1.6"
                }}
            >
                <LevelItem><b>Rodzaj:</b></LevelItem>
            </Level>
            <Level
                style={{
                    paddingBottom: "5px",
                }}
            >
                <LevelItem>
                {/* {(row?.raw?.sheet_types || []).map((type) => (
                    <span key={type.uid}>
                        {Icons[type.icon_name] ? (
                        <Tooltip content={<div>{type.description}</div>}>
                            {Icons[type.icon_name]}
                        </Tooltip>
                        ) : (
                        <Tooltip content={<div>{type.description}</div>}>
                            <Label
                            variant="outline"
                            style={{ marginRight: "5px" }}
                            >
                            {type.sheet_type.toUpperCase()}
                            </Label>
                        </Tooltip>
                        )}
                    </span>
                ))} */}
                    <SheetTypesCell row={row} Icons={Icons} groupFilter={groupFilter} />
                </LevelItem>
            </Level>
            <Divider />
            </>
            }
            {hasGroups && context.state.isAuthUser &&
            <>
            <Level
                style={{
                    paddingTop: "10px",
                    paddingBottom: "10px",
                    fontSize: "13px",
                    // color: "#cfcfcf",
                    lineHeight: "1.6"
                }}
            >
                <LevelItem><b>Grupy:</b></LevelItem>
                <LevelItem>
                    <GroupsCell row={row} groupFilter={groupFilter} />
                </LevelItem>
            </Level>
            <Divider />
            </>
            }
            {hasSolutions && (
            <>
            <Level
                style={{
                    paddingTop: "10px",
                    fontSize: "13px",
                    // color: "#cfcfcf",
                    lineHeight: "1.6"
                }}
            >
                <LevelItem><b>Rozwiązania:</b></LevelItem>
                <LevelItem>
                    <DevSolutionsCell row={row} props={props} context={context} />
                </LevelItem>
            </Level>
            <Level
                style={{
                    paddingTop: "10px",
                    fontSize: "13px",
                    // color: "#cfcfcf",
                    lineHeight: "1.6"
                }}
            >
                <LevelItem><b>Udostępnij rozwiązania:</b></LevelItem>
                <LevelItem>
                    <ShareCell row={row} props={props} setSheets={setSheets} branch={branch} context={context} />
                </LevelItem>
            </Level>
            </>
            )}
        </CardBody>
        {/* FULLSCREEN PREVIEW */}
        <Modal
            isOpen={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
            variant="large"
            onEscapePress={(event) => {
                event.stopPropagation();
                setIsPreviewOpen(false);
            }}
        >
            <ModalHeader
                // onClick={() => setIsPreviewOpen(false)}
                title={
                    <>
                    Id: {row.id}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; {getMonths(row.exam_month)} {row.year} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Nr: {row.raw?.task_number?.toString().padStart(2, "0")} 
                    </>
                }
                labelId="basic-modal-title" 
                // description={`[ ${row.year} ${row.exam_month} Nr: ${row.raw.task_number} ]`}
            />
            <ModalBody
                onClick={() => setIsPreviewOpen(false)}
                style={{
                    textAlign: "center",
                    // background: "#0f0f0f"
                }}>
                <img
                    src={imgSrc}
                    alt={sheetName}
                    style={{
                        maxWidth: "100%",
                        maxHeight: "85vh",
                        borderRadius: "16px",
                        boxShadow: "0 40px 120px rgba(0,0,0,.8)"
                    }}
                />
                <Level
                    style={{
                        paddingTop: "10px",
                        paddingBottom: "5px",
                        fontSize: "13px",
                        // color: "#cfcfcf",
                        lineHeight: "1.6"
                    }}
                >
                    <LevelItem>
                        <SheetNameCell row={row} props={props} context={context} withMiniature={false} withPopover={false} />
                    </LevelItem>
                </Level>
            </ModalBody>
            {/* <ModalFooter>
                <Level
                    style={{
                        paddingTop: "10px",
                        paddingBottom: "5px",
                        fontSize: "13px",
                        color: "#cfcfcf",
                        lineHeight: "1.6"
                    }}
                >
                    <LevelItem>
                        <Content
                            component={ContentVariants.a}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDownload();
                            }}
                            style={{
                                cursor: "pointer",
                                fontWeight: 600
                            }}
                        >
                            {sheetName}
                            </Content>
                            <CopyText
                                text={sheetName}
                                id={row.id}
                                style={{ marginLeft: "10px" }}
                            />
                    </LevelItem>
                </Level>
            </ModalFooter> */}
        </Modal>
        <style>
            {`
            @keyframes shimmer {
                0% { background-position: -200% 0 }
                100% { background-position: 200% 0 }
            }
            .ultra-card-image img {
                will-change: transform, filter;
            }
            .ultra-card-image:hover img {
                transform: scale(1.02);
                filter: grayscale(0%) brightness(110%);
            }
            .ultra-card-image:hover div:nth-child(3) {
                opacity: 1;
            }
            `}
        </style>
        </>
    );
};

